import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/**
 * Data hooks for the Contracts feature — thin wrappers over the Ops proxy
 * routes (`/api/contracts/*`), which forward live to the Cinch billing API.
 * `keepPreviousData` keeps the current page visible while the next loads, so
 * paginating/sorting doesn't flash empty.
 */

export interface ListParams {
  limit: number;
  offset: number;
  ordering?: string;
  search?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function qs(params: ListParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}

function useList<T>(tab: string, path: string, params: ListParams, enabled = true) {
  return useQuery({
    queryKey: ["contracts", tab, params],
    queryFn: () => apiFetch<Paginated<T>>(`/contracts/${path}?${qs(params)}`),
    enabled,
    placeholderData: keepPreviousData,
  });
}

// ── Row shapes (only the fields the tables render) ───────────────────────────
export interface ContractRow {
  id: string;
  company_name: string | null;
  company_dba: string | null;
  contract_type_name: string | null;
  mrr_total: number | null;
  term_months: number | null;
  payment_type: string | null;
  sign_date: string | null;
  renewal_date: string | null;
}

export interface AccountRow {
  id: string;
  name: string | null;
  quickbooks_id: number | null;
  created_when: string | null;
  updated_when: string | null;
}

export interface CompanyRow {
  id: string;
  name: string | null;
  account_name: string | null;
  is_active: boolean | null;
}

export interface ItemRow {
  id: string;
  name: string | null;
  // CIN-6317: catalog category ("subscription" | "usage" | "pass_thru" | "services" | "other").
  category: string | null;
  // CIN-6317: Integration.key values this item represents (public-schema keys, no FK).
  integration_keys: string[] | null;
  price_metric: string | null;
  price_metric_name: string | null;
  unit_cost: number | null;
  floor_price: number | null;
  starting_price: number | null;
  recommended_price: number | null;
  // Detail-drawer fields (present on /billing/items rows; harmless if absent on a list).
  descr?: string | null;
  customer_facing_descr?: string | null;
  sku_cinch?: string | null;
  quickbooks_item_id?: number | null;
  is_archived?: boolean | null;
}

// ── Packages (Cinch /billing/packages → PackageSerializer) ───────────────────
export interface PackageItemRow {
  id?: string;
  item?: string | null;
  item_detail?: { name?: string | null } | null;
  quantity_limit?: number | null;
  included_price?: number | null;
  display_as_unlimited?: boolean | null;
}

export interface PackageRow {
  id: string;
  name: string | null;
  sku: string | null;
  descr?: string | null;
  customer_facing_descr?: string | null;
  price: number | null;
  // FRACTION 0–1 (e.g. 0.42 = 42%), not a percentage.
  estimated_gross_margin: number | null;
  pricing_type: string | null; // "flat_fee" | "per_unit"
  proposal_eligible: boolean | null;
  price_metric?: string | null;
  price_metric_name: string | null;
  package_items?: PackageItemRow[];
  quickbooks_item_id?: number | null;
  is_archived?: boolean | null;
}

// ── Discounts (Cinch /billing/discounts → DiscountSerializer) ────────────────
export interface DiscountUnitGrantRow {
  id?: string;
  price_metric?: string | null;
  quantity?: number | null;
  recurrence?: string | null; // "monthly" | "one_time"
}

export interface DiscountRow {
  id: string;
  name: string | null;
  description?: string | null;
  customer_facing_descr?: string | null;
  type: string | null; // percentage | fixed | tiered | time_based | quantity_based
  value: number | null;
  code: string | null;
  stackable: boolean | null;
  applicable_items?: string[];
  applicable_packages?: string[];
  min_quantity?: number | null;
  max_quantity?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  duration_periods?: number | null;
  period_unit?: string | null;
  unit_grants?: DiscountUnitGrantRow[];
  is_archived?: boolean | null;
}

export interface ContractTypeRow {
  id: string;
  name: string | null;
  descr: string | null;
  contract_count: number | null;
}

export interface PriceMetricRow {
  id: string;
  name: string | null;
  description: string | null;
  support_tiers: boolean | null;
}

export interface QbCustomerRow {
  Id: string;
  CompanyName: string | null;
  GivenName: string | null;
  FamilyName: string | null;
  PrimaryEmailAddr: string | null;
  account: { id: string; name: string } | null;
}

export interface AccountsEnrichment {
  refreshed_at: string;
  accounts: Record<string, { stripe_customer_ids: string[]; is_active: boolean }>;
}

// ── Hooks (one per tab; inactive tabs aren't mounted, so they don't fetch) ───
export const useContracts = (p: ListParams) => useList<ContractRow>("contracts", "contracts", p);
export const useAccounts = (p: ListParams) => useList<AccountRow>("accounts", "accounts", p);
export const useCompanies = (p: ListParams) => useList<CompanyRow>("companies", "companies", p);
export const useItems = (p: ListParams) => useList<ItemRow>("items", "items", p);
export const usePackages = (p: ListParams) => useList<PackageRow>("packages", "packages", p);
export const useDiscounts = (p: ListParams) => useList<DiscountRow>("discounts", "discounts", p);
export const useContractTypes = (p: ListParams) =>
  useList<ContractTypeRow>("contract-types", "contract-types", p);
export const usePriceMetrics = (p: ListParams) =>
  useList<PriceMetricRow>("price-metrics", "price-metrics", p);
export const useQuickbooks = (p: ListParams) => useList<QbCustomerRow>("quickbooks", "quickbooks", p);

export interface Closer {
  id: string;
  name: string | null;
  email?: string | null;
  hubspot_owner_id?: string | null;
}
export const useClosers = () => useList<Closer>("closers", "closers", { limit: 500, offset: 0 });

/** Filtered contract picker for the editor's replaced/amended/reseller fields
 *  (own cache key so it never clobbers the main Contracts list). Only fetches
 *  when `enabled` (i.e. the relevant contract-type is selected + an account chosen). */
export function useContractPicker(params: ListParams, enabled: boolean) {
  return useList<ContractRow>("contracts-picker", "contracts", params, enabled);
}
/** Company picker for the editor's assigned-company field (Purchase Order type). */
export function useCompanyPicker(enabled: boolean) {
  return useList<CompanyRow>("companies-picker", "companies", { limit: 2000, offset: 0 }, enabled);
}

// ── Detail (single-record) shapes + hooks ───────────────────────────────────
export interface ContractLineItem {
  uuid?: string;
  // The Cinch item UUID (same id-space as the proposals catalog `/billing/items`).
  // Used to match a contract line to a current catalog item when building a proposal.
  item?: string | null;
  // CIN-6317 phase 4: a line references an item XOR a package.
  package?: string | null;
  qty?: number | null;
  price_per_unit?: number | null;
  overage_price_per_unit?: number | null;
  item_total?: number | null;
  item_detail?: { name?: string | null } | null;
  package_detail?: { name?: string | null } | null;
}

export interface RelatedContractRef {
  id: string;
  company_name?: string | null;
  sign_date?: string | null;
  // Emitted by Cinch's ContractSimpleSerializer — enough to layer an upsell onto the
  // timeline (value from its effective month) or value a replaced contract.
  billing_effective_from?: string | null;
  mrr_total?: number | null;
  contract_type_name?: string | null;
}

export type RenewalTerm = "SAME_TERM" | "DIFFERENT_TERM" | "MONTH_TO_MONTH";

/** A discount applied to a contract (mirrors Cinch's ContractDiscountSerializer). Only
 *  percentage / fixed / time_based reduce the projection deterministically. */
export interface ContractDiscount {
  discount_detail?: {
    type?: "percentage" | "fixed" | "time_based" | "tiered" | "quantity_based" | string | null;
    value?: number | null;
    duration_periods?: number | null;
  } | null;
  duration_override?: number | null;
  // The line this discount targets (null = whole-contract). Cinch keys line items by uuid.
  target_line_item?: string | null;
}

export interface ContractDetail {
  id: string;
  company_name?: string | null;
  company_dba?: string | null;
  account?: string | null;
  account_name?: string | null;
  // Writable FK ids (Cinch serializer is fields="__all__"); needed to prefill the editor.
  contract_type?: string | null;
  contract_type_name?: string | null;
  replaced_contract_id?: string | null;
  amended_contract?: string | null;
  reseller_contract_id?: string | null;
  closer_id?: string | null;
  lead_source?: string | null;
  mrr_total?: number | null;
  term_months?: number | null;
  payment_type?: string | null;
  base_fee?: number | null;
  billing_effective_from?: string | null;
  billing_effective_through?: string | null;
  sign_date?: string | null;
  renewal_date?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  email?: string | null;
  bcc_email?: string | null;
  cc_email?: string | null;
  phone?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  initial_contacts?: number | null;
  ten_k_more?: number | null;
  use_email_count?: boolean | null;
  custom_terms?: boolean | null;
  // Renewal lifecycle (already emitted by Cinch's ContractSerializer, fields="__all__").
  is_auto_renew?: boolean | null;
  renewal_term?: RenewalTerm | null;
  renewal_term_months?: number | null;
  mtm_uplift_pct?: number | null;
  // CIN-6317 phase 5: notice + debook lifecycle + computed next renewal.
  notice_period_days?: number | null;
  next_renewal_when?: string | null;
  debooked?: boolean | null;
  debooked_reason?: string | null;
  additional_terms?: string | null;
  notes?: string | null;
  contract_status?: string | null;
  contract_discounts?: ContractDiscount[];
  contract_line_items?: ContractLineItem[];
  closer_name?: string | null;
  closer_hubspot_owner_id?: string | null;
  pandadoc_id?: string | null;
  hubspot_deal_id?: string | null;
  stripe_customer_id?: string | null;
  assigned_company_name?: string | null;
  assigned_company_id?: string | null;
  // Related-contract links (arrays for the "…_by_contracts", single objects for "…_details").
  replaced_by_contracts?: RelatedContractRef[];
  replacement_contract_details?: RelatedContractRef | null;
  amended_by_contracts?: RelatedContractRef[];
  amendment_contract_details?: RelatedContractRef | null;
  reseller_by_contracts?: RelatedContractRef[];
  reseller_contract_details?: RelatedContractRef | null;
}

export function useContractDetail(id: string | null) {
  return useQuery({
    queryKey: ["contracts", "contract", id],
    queryFn: () => apiFetch<ContractDetail>(`/contracts/contracts/${id}`),
    enabled: !!id,
  });
}

/** Commission-oriented valuation of a replacement contract: the new (or contraction)
 *  value it created, and the ARR increase over the greater-of floor (prior-month billed
 *  actuals vs replaced-contract ARR). `is_replacement` is false for a normal contract. */
export interface ReplacementValueOut {
  is_replacement: boolean;
  replacement_mrr: number;
  replaced_mrr?: number | null;
  prior_month_billed_mrr?: number | null;
  prior_month_period?: string | null;
  // The greater-of baseline (what the account already had) the increase is measured against.
  baseline_monthly?: number | null;
  baseline_arr?: number | null;
  // The value the replacement created = increase over the baseline; this IS the commission basis.
  new_value_mrr?: number | null;
  new_value_arr?: number | null;
  data_source?: "snowflake" | "stale-cache" | "unavailable";
}

/** Only fetch when the caller already knows it's a replacement (avoids a Snowflake round
 *  trip for every contract opened). */
export function useReplacementValue(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["contracts", "replacement-value", id],
    queryFn: () => apiFetch<ReplacementValueOut>(`/contracts/contracts/${id}/replacement-value`),
    enabled: !!id && enabled,
  });
}

export interface AccountCompanyRef {
  id: string;
  name?: string | null;
}

export interface AccountContractRef {
  id: string;
  company_name?: string | null;
  sign_date?: string | null;
  mrr_total?: number | null;
  billing_effective_through?: string | null;
  stripe_customer_id?: string | null;
}

export interface AccountDetail {
  id: string;
  name?: string | null;
  quickbooks_id?: number | null;
  quickbooks_customer_name?: string | null;
  created_when?: string | null;
  updated_when?: string | null;
  companies?: AccountCompanyRef[];
  contracts?: AccountContractRef[];
}

export function useAccountDetail(id: string | null) {
  return useQuery({
    queryKey: ["contracts", "account", id],
    queryFn: () => apiFetch<AccountDetail>(`/contracts/accounts/${id}`),
    enabled: !!id,
  });
}

export interface CompanyDetail {
  id: string;
  name?: string | null;
  account?: string | null;
  account_name?: string | null;
  is_active?: boolean | null;
  status_name?: string | null;
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  phone?: string | null;
  created_when?: string | null;
  updated_when?: string | null;
}

export function useCompanyDetail(id: string | null) {
  return useQuery({
    queryKey: ["contracts", "company", id],
    queryFn: () => apiFetch<CompanyDetail>(`/contracts/companies/${id}`),
    enabled: !!id,
  });
}

export function useAccountsEnrichment() {
  return useQuery({
    queryKey: ["contracts", "accounts", "enrichment"],
    queryFn: () => apiFetch<AccountsEnrichment>("/contracts/accounts/enrichment"),
  });
}

/** Force the server to rebuild the enrichment cache (skips its 15-min TTL). */
export function fetchAccountsEnrichment(force: boolean): Promise<AccountsEnrichment> {
  return apiFetch<AccountsEnrichment>(`/contracts/accounts/enrichment${force ? "?refresh=true" : ""}`);
}

// ── Contract-type playbook overrides (Ops-persisted, admin-edited) ───────────
/** An Ops-stored playbook for a contract type. `data` is the camelCase playbook
 *  blob the FE overlays on the static archetype default (see playbook/content). */
export interface StoredPlaybook {
  data: Record<string, unknown>;
  updatedByName: string | null;
  updatedAt: string | null;
}

/** All stored playbooks, keyed by contract-type id. */
export function useContractTypePlaybooks() {
  return useQuery({
    queryKey: ["contracts", "contract-type-playbooks"],
    queryFn: () => apiFetch<Record<string, StoredPlaybook>>("/contracts/contract-types/playbooks"),
  });
}

/** Create/replace a contract type's playbook (gated server-side by contracts.write). */
export function useSaveContractTypePlaybook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      apiFetch<StoredPlaybook>(`/contracts/contract-types/${id}/playbook`, { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contracts", "contract-type-playbooks"] }),
  });
}

// ── Write hooks (proxy to Cinch billing; gated contracts.write + Cinch superuser) ──
// A save with id === null POSTs (create); otherwise PATCHes (partial update). Nested
// arrays (package_items / unit_grants / contract_line_items / contract_discounts) are
// delete-and-recreate wholesale on Cinch's side when present in the body.
type WriteBody = Record<string, unknown>;
type SaveArgs = { id: string | null; body: WriteBody };

/** One factory for the item/package/discount CRUD trio — same shape, different path. */
function useResourceWrites<T>(resource: "items" | "packages" | "discounts" | "contracts") {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["contracts", resource] });
  const save = useMutation({
    mutationFn: ({ id, body }: SaveArgs) =>
      id
        ? apiFetch<T>(`/contracts/${resource}/${id}`, { method: "PATCH", body })
        : apiFetch<T>(`/contracts/${resource}`, { method: "POST", body }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/contracts/${resource}/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
  return { save, remove, invalidate };
}

/** Archive/unarchive is only defined for the catalog trio (not contracts). */
function useArchive<T>(resource: "items" | "packages" | "discounts") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiFetch<T>(`/contracts/${resource}/${id}/archive?archived=${archived}`, { method: "PATCH" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contracts", resource] }),
  });
}

export function useItemWrites() {
  return { ...useResourceWrites<ItemRow>("items"), archive: useArchive<ItemRow>("items") };
}
export function usePackageWrites() {
  return { ...useResourceWrites<PackageRow>("packages"), archive: useArchive<PackageRow>("packages") };
}
export function useDiscountWrites() {
  return { ...useResourceWrites<DiscountRow>("discounts"), archive: useArchive<DiscountRow>("discounts") };
}
export function useContractWrites() {
  return useResourceWrites<ContractDetail>("contracts");
}
